(() => {
  // Safe when the section asset is emitted more than once on the same page.
  if (customElements.get('about-section')) return;
class AboutSection extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._cleanup = [];
    this._timers = new Set();
  }

  connectedCallback() {
    this.destroyRuntime();
    this.initReveal();
    this.initTouchInteractions();
    this.initThemeEditor();
  }

  disconnectedCallback() {
    this.destroyRuntime();
  }

  destroyRuntime() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this._cleanup.forEach((fn) => fn());
    this._cleanup = [];
    this._timers.forEach((timer) => window.clearTimeout(timer));
    this._timers.clear();
  }

  initReveal() {
    const targets = Array.from(this.querySelectorAll('[data-about-reveal]'));
    if (!targets.length) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const mode = this.dataset.entryAnimation || 'fade-up';

    if (mode === 'none') {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    if (reduced) this.classList.add('about--reduced-motion');
    else this.classList.remove('about--reduced-motion');
    targets.forEach((target) => target.classList.remove('is-visible'));

    if (!('IntersectionObserver' in window)) {
      requestAnimationFrame(() => requestAnimationFrame(() => targets.forEach((target) => target.classList.add('is-visible'))));
      return;
    }

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        requestAnimationFrame(() => entry.target.classList.add('is-visible'));
        this._observer?.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

    requestAnimationFrame(() => requestAnimationFrame(() => {
      targets.forEach((target) => this._observer?.observe(target));
    }));
  }

  pulseElement(element, duration = 620) {
    if (!element) return;
    element.classList.remove('is-pressed');
    void element.offsetWidth;
    element.classList.add('is-pressed');
    const timer = window.setTimeout(() => {
      element.classList.remove('is-pressed');
      this._timers.delete(timer);
    }, duration);
    this._timers.add(timer);
  }

  initTouchInteractions() {
    const mediaFrame = this.querySelector('[data-about-media-frame]');
    const interactiveItems = Array.from(this.querySelectorAll('[data-about-interactive]'));

    const onPointerDown = (event) => {
      if (event.pointerType === 'mouse') return;
      const item = event.currentTarget;
      this.pulseElement(item, item === mediaFrame ? 720 : 620);
    };

    const onPointerEnter = (event) => {
      if (event.pointerType === 'touch') return;
      event.currentTarget.classList.add('is-pointer-hover');
    };

    const onPointerLeave = (event) => {
      event.currentTarget.classList.remove('is-pointer-hover');
    };

    const bindInteractivePointer = (element) => {
      element.addEventListener('pointerdown', onPointerDown, { passive: true });
      element.addEventListener('pointerenter', onPointerEnter, { passive: true });
      element.addEventListener('pointerleave', onPointerLeave, { passive: true });
      this._cleanup.push(() => {
        element.removeEventListener('pointerdown', onPointerDown);
        element.removeEventListener('pointerenter', onPointerEnter);
        element.removeEventListener('pointerleave', onPointerLeave);
        element.classList.remove('is-pointer-hover');
      });
    };

    if (mediaFrame && (this.dataset.imageInteraction || 'none') !== 'none') {
      bindInteractivePointer(mediaFrame);
    }

    interactiveItems.forEach(bindInteractivePointer);
  }

  initThemeEditor() {
    const replayReveal = () => {
      const targets = Array.from(this.querySelectorAll('[data-about-reveal]'));
      targets.forEach((target) => target.classList.remove('is-visible'));
      requestAnimationFrame(() => requestAnimationFrame(() => {
        targets.forEach((target) => target.classList.add('is-visible'));
      }));
    };

    const onSectionSelect = (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      replayReveal();
    };

    const clearEditorFocus = () => {
      this.querySelectorAll('.is-editor-focus').forEach((node) => node.classList.remove('is-editor-focus'));
    };

    const onBlockSelect = (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      clearEditorFocus();
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const target = this.querySelector(`[data-about-block-id="${CSS.escape(blockId)}"]`);
      if (!target) return;
      target.classList.add('is-editor-focus');
      if (target.matches('[data-about-interactive]')) this.pulseElement(target, 620);
    };

    const onBlockDeselect = (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      clearEditorFocus();
    };

    document.addEventListener('shopify:section:select', onSectionSelect);
    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:block:deselect', onBlockDeselect);

    this._cleanup.push(() => document.removeEventListener('shopify:section:select', onSectionSelect));
    this._cleanup.push(() => document.removeEventListener('shopify:block:select', onBlockSelect));
    this._cleanup.push(() => document.removeEventListener('shopify:block:deselect', onBlockDeselect));
  }
}

if (!customElements.get('about-section')) {
  customElements.define('about-section', AboutSection);
}

})();
